import { useCreateOrUpdateSculptureEditionInExhibition } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import { FormLayoutElement } from "@/config/forms/general";
import { SculptureEditionInExhibition } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const defaultFields: FormLayoutElement[] = [
  {
    name: "sculptureEditionDansExpositionsNumeroCatalogue",
    type: "text",
    label: "N° catalogue",
    className: "w-full",
  } as FormLayoutElement,
  {
    name: "sculptureEditionDansExpositionsNotes",
    type: "richtext",
    label: "Notes",
    className: "w-full",
  } as FormLayoutElement,
];

const SculptureEditionInExhibitionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: SculptureEditionInExhibition;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    sculptureEditionDansExpositionsNumeroCatalogue: z.string().optional(),
    sculptureEditionDansExpositionsNotes: z.string().optional(),
    exhibition: z
      .object({
        expositionsDescriptionNumeroExpo: z.string().optional(),
        expositionsDescriptionResume: z.string().optional(),
      })
      .optional(),
    sculptureEdition: z
      .object({
        sculptureEditionNumeroEnregistrement: z.string().optional(),
        sculptureEditionNotes: z.string().optional(),
      })
      .optional(),
  });

  const { mutate: createOrUpdate } =
    useCreateOrUpdateSculptureEditionInExhibition(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      exhibition: data.exhibition,
      sculptureEdition: data.sculptureEdition,
      sculptureEditionDansExpositionsNumeroCatalogue:
        data.sculptureEditionDansExpositionsNumeroCatalogue ?? "",
      sculptureEditionDansExpositionsNotes:
        data.sculptureEditionDansExpositionsNotes ?? "",
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    const sculptureEditionId = data.sculptureEdition?.id;
    const exhibitionId = data.exhibition?.id;
    if (!sculptureEditionId || !exhibitionId) return;

    createOrUpdate({
      id: data.id,
      sculptureEditionId,
      exhibitionId,
      sculptureEditionDansExpositionsNumeroCatalogue:
        formData.sculptureEditionDansExpositionsNumeroCatalogue,
      sculptureEditionDansExpositionsNotes:
        formData.sculptureEditionDansExpositionsNotes,
    });
  };

  const isFromExhibition = type === "exhibition";
  const previewConfig = isFromExhibition
    ? {
        attachments: data.sculptureEdition?.attachments ?? [],
        type: "sculptureEdition" as const,
        alt:
          data.sculptureEdition?.sculptureEditionNumeroEnregistrement ??
          data.sculptureEdition?.sculptureEditionNumeroInventaire,
      }
    : {
        attachments: data.exhibition?.attachments ?? [],
        type: "exhibition" as const,
        alt:
          data.exhibition?.expositionsDescriptionNumeroExpo ??
          data.exhibition?.expositionsDescriptionTitre,
      };

  const layoutFields =
    fields && fields.length > 0 ? fields : defaultFields;

  return (
    <LinksDrawerLayout
      form={form}
      fields={layoutFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default SculptureEditionInExhibitionDrawer;
